/**
 * MascotChat — widget de chat IA flottant des ambassadeurs KAIZEN (KAI · MEI · LANA).
 * mode="public"  → visiteurs (page d'accueil) : info dojo, disciplines, cours d'essai payant.
 * mode="member"  → membres connectés : guide horaires, inscriptions, paiements.
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { ChatCircleDots, X, PaperPlaneRight } from "@phosphor-icons/react";
import { DraggableWidget } from "./ui/DraggableWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
  { id: "KAI", tag: "Le guerrier", accent: "#c9a961", hi: "Salut, c'est KAI. Discipline, respect, dépassement — comment puis-je te guider aujourd'hui ?" },
  { id: "MEI", tag: "PowerKick Her", accent: "#ec4899", hi: "Coucou, moi c'est MEI ! 💪 Prête à découvrir le kickboxing au féminin ? Pose-moi tes questions !" },
  { id: "LANA", tag: "La communauté", accent: "#a855f7", hi: "Bonjour, je suis LANA — bienvenue dans la grande famille KAIZEN ! Que puis-je faire pour toi ?" },
];

export default function MascotChat({ mode = "public" }) {
  const [open, setOpen] = useState(false);
  const [persona, setPersona] = useState("KAI");
  const [sessions, setSessions] = useState({}); // persona -> session_id
  const [msgs, setMsgs] = useState({ KAI: [], MEI: [], LANA: [] });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ full_name: "", email: "", phone: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const scrollRef = useRef(null);

  const current = PERSONAS.find((p) => p.id === persona);
  const list = msgs[persona] || [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [list, open, persona, showLead]);

  async function submitLead() {
    if (!lead.full_name.trim() || !lead.email.includes("@")) return;
    setLeadBusy(true);
    try {
      const r = await axios.post(`${API}/mascot/lead`, {
        ...lead, persona, session_id: sessions[persona] || null,
        interest: (list.find((m) => m.role === "user") || {}).content || "",
      });
      setLeadDone(true);
      setShowLead(false);
      setMsgs((m) => ({ ...m, [persona]: [...(m[persona] || []), { role: "assistant", content: r.data.message }] }));
    } catch {
      setMsgs((m) => ({ ...m, [persona]: [...(m[persona] || []), { role: "assistant", content: "Hmm, je n'ai pas pu enregistrer. Réessaie ou écris-nous directement 🙏" }] }));
    }
    setLeadBusy(false);
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => ({ ...m, [persona]: [...(m[persona] || []), { role: "user", content: text }] }));
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mascot/chat`, {
        persona, message: text, mode, session_id: sessions[persona] || null,
      });
      setSessions((s) => ({ ...s, [persona]: r.data.session_id }));
      setMsgs((m) => ({ ...m, [persona]: [...(m[persona] || []), { role: "assistant", content: r.data.reply }] }));
    } catch {
      setMsgs((m) => ({ ...m, [persona]: [...(m[persona] || []), { role: "assistant", content: "Oups, je suis indisponible un instant. Réessaie ! 🙏" }] }));
    }
    setBusy(false);
  }

  return (
    <DraggableWidget id="mascot-chat" defaultStyle={{ bottom: 96, right: 20 }}>
      {/* Bouton flottant */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="mascot-chat-launcher"
          aria-label="Discuter avec un ambassadeur KAIZEN"
          className="relative h-14 w-14 rounded-full flex items-center justify-center shadow-2xl shadow-black/40 transition-transform hover:scale-110"
          style={{ background: "linear-gradient(135deg,#c9a961,#a8852f)" }}
        >
          <ChatCircleDots size={26} weight="fill" className="text-[#0a0a0a]" />
          <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-[#D90429] border-2 border-[#0a0a0a] animate-pulse" />
        </button>
      )}

      {/* Panneau */}
      {open && (
        <div
          data-testid="mascot-chat-panel"
          className="w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-[#0b0b0b] shadow-2xl shadow-black/60 cursor-auto"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* En-tête */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/10" style={{ background: "#111" }}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: current.accent }}>Ambassadeur KAIZEN</div>
              <div className="text-white font-black text-lg leading-none" style={{ fontFamily: "'Barlow Condensed',sans-serif" }}>{persona}</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="mascot-chat-close" className="text-white/60 hover:text-white p-1"><X size={20} /></button>
          </div>

          {/* Onglets personas */}
          <div className="flex gap-1 px-3 py-2 border-b border-white/10 bg-[#0d0d0d]">
            {PERSONAS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPersona(p.id)}
                data-testid={`mascot-tab-${p.id}`}
                className="flex-1 py-1.5 rounded-lg text-xs font-bold tracking-wide transition-colors"
                style={persona === p.id
                  ? { background: p.accent, color: "#0a0a0a" }
                  : { background: "transparent", color: "rgba(255,255,255,0.5)" }}
              >
                {p.id}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="mascot-chat-messages">
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm px-3 py-2 text-sm bg-[#1a1a1a] text-white/85">
                {current.hi}
              </div>
            </div>
            {list.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "rounded-tr-sm text-[#0a0a0a]" : "rounded-tl-sm bg-[#1a1a1a] text-white/85"}`}
                  style={m.role === "user" ? { background: current.accent } : undefined}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-tl-sm px-3 py-2 bg-[#1a1a1a] text-white/50 text-sm">…</div>
              </div>
            )}

            {/* Formulaire de capture d'essai payant */}
            {showLead && !leadDone && (
              <div className="rounded-xl border border-white/10 bg-[#141414] p-3 space-y-2" data-testid="mascot-lead-form">
                <div className="text-xs font-semibold text-white/80">🥋 Réserve ton cours d'essai (19,99 $)</div>
                <input value={lead.full_name} onChange={(e) => setLead({ ...lead, full_name: e.target.value })}
                  placeholder="Ton nom" data-testid="lead-name"
                  className="w-full bg-[#1f1f1f] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
                <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="Ton courriel" type="email" data-testid="lead-email"
                  className="w-full bg-[#1f1f1f] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
                <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                  placeholder="Téléphone (optionnel)" data-testid="lead-phone"
                  className="w-full bg-[#1f1f1f] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
                <button onClick={submitLead} disabled={leadBusy || !lead.full_name.trim() || !lead.email.includes("@")}
                  data-testid="lead-submit"
                  className="w-full rounded-lg py-2 text-sm font-bold text-[#0a0a0a] disabled:opacity-40"
                  style={{ background: current.accent }}>
                  {leadBusy ? "Envoi…" : "Envoyer ma demande"}
                </button>
              </div>
            )}
          </div>

          {/* CTA cours d'essai */}
          {mode === "public" && !leadDone && (
            <button onClick={() => setShowLead((s) => !s)} data-testid="mascot-lead-cta"
              className="mx-3 mb-2 rounded-lg py-2 text-xs font-bold tracking-wide transition-colors"
              style={{ background: showLead ? "#1a1a1a" : current.accent, color: showLead ? "rgba(255,255,255,.6)" : "#0a0a0a" }}>
              {showLead ? "Masquer" : "🥋 Réserver mon cours d'essai — 19,99 $"}
            </button>
          )}

          {/* Saisie */}
          <div className="p-3 border-t border-white/10 bg-[#0d0d0d] flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={`Écris à ${persona}…`}
              data-testid="mascot-chat-input"
              className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid="mascot-chat-send"
              className="h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105"
              style={{ background: current.accent }}
            >
              <PaperPlaneRight size={16} weight="fill" className="text-[#0a0a0a]" />
            </button>
          </div>
        </div>
      )}
    </DraggableWidget>
  );
}
